import React, { useMemo } from 'react';
import moment from 'moment';
import { ModalsKeys } from 'global';
import { formatCurrency } from 'utils';
import { useModal } from 'hooks/useModal';
import { useQuery } from '@apollo/client';
import { grey } from '@mui/material/colors';
import { DateFormats } from 'constants/common';
import { CustomDialog } from 'components/shared/Dialog';
import { Country } from '@trnsact/trnsact-shared-types';
import { ExpandableList } from 'components/shared/ExpandableList';
import { useCountries, useRegions } from 'modules/geo';
import { capitalizeFirstLetter, formatEnumValue } from 'formatters';
import { Person, CalendarToday, Update, Public } from '@mui/icons-material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { Box, Chip, CircularProgress, Stack, Typography, Button, Divider } from '@mui/material';

import { DealSheetConfigurationQuery, DealSheetConfigurationViewModel } from '../../types';

type ChargeItem = DealSheetConfigurationViewModel['charges']['data'][number];

export const ConfigurationDetailsDialog = () => {
  const { isOpen, data, handleClose } = useModal(ModalsKeys.DealSheetRuleDetailsDialog);

  const {
    error,
    loading,
    data: response,
  } = useQuery<DealSheetConfigurationQuery>(data.document, {
    skip: !data?.configId,
    variables: {
      input: {
        id: data?.configId,
      },
    },
  });

  const { countries } = useCountries();
  const { regions: usRegions } = useRegions(Country.Us);
  const { regions: canadaRegions } = useRegions(Country.Ca);

  const dictionaryMap = useMemo(() => {
    const map = new Map<string, string>();

    [...countries, ...usRegions, ...canadaRegions].forEach(({ value, label }) => {
      map.set(value, label);
    });

    return map;
  }, [countries, usRegions, canadaRegions]);

  const resolveLabel = (value: string) => dictionaryMap.get(value) ?? formatEnumValue(value);

  const config = response?.dealSheets?.result?.entity ?? null;
  const ruleEntries = Object.entries(config?.dealRules ?? {}).filter(
    ([, rules]) => !!rules && typeof rules !== 'string'
  );
  const charges = config?.charges?.data?.filter(charge => charge.type === 'CHARGE') ?? [];
  const taxes = config?.charges?.data?.filter(charge => charge.type === 'TAX') ?? [];

  return (
    <CustomDialog
      maxWidth="md"
      isOpen={isOpen}
      onClose={handleClose}
      label="Business Rule Details"
      actions={<Button onClick={handleClose}>Cancel</Button>}
    >
      <RenderWithFallback
        error={error}
        isLoading={loading}
        fallBack={
          <Stack alignItems="center" justifyContent="center" minHeight="200px">
            <CircularProgress />
          </Stack>
        }
      >
        <RenderWithCondition condition={!!config}>
          <Stack gap="1rem">
            <Stack gap="0.5rem">
              <Stack gap="0.2rem">
                <Typography variant="h6" component="span">
                  Rule: {config?.name}
                </Typography>

                {config?.description && (
                  <Typography variant="body2" color="text.secondary">
                    {config?.description}
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" gap={2} flexWrap="wrap">
                <Chip
                  icon={<Public fontSize="small" color="primary" />}
                  color={config?.isGlobal ? 'primary' : 'default'}
                  label={<Typography>{config?.isGlobal ? 'Global Rule' : 'Custom Rule'}</Typography>}
                />

                {config?.createdBy && (
                  <Chip
                    icon={<Person fontSize="small" color="primary" />}
                    label={<Typography>{`Author: ${config?.createdBy.fullName}`}</Typography>}
                  />
                )}

                <Chip
                  icon={<CalendarToday fontSize="small" color="primary" />}
                  label={
                    <Typography>{`Created: ${moment(config?.createdDateTime).format(DateFormats.DateTimeMedium24Hour)}`}</Typography>
                  }
                />

                {config?.updatedDateTime && (
                  <Chip
                    icon={<Update fontSize="small" color="primary" />}
                    label={
                      <Typography>{`Updated: ${moment(config?.updatedDateTime).format(DateFormats.DateTimeMedium24Hour)}`}</Typography>
                    }
                  />
                )}
              </Stack>
            </Stack>

            <Divider />

            <RenderWithCondition condition={!!ruleEntries.length}>
              <Stack gap="0.5rem">
                <Typography variant="h6" component="span">
                  Conditions
                </Typography>

                <Stack gap="1rem">
                  {ruleEntries.map(([ruleGroup, rules]: [string, Record<string, unknown>]) => {
                    const ruleItems = Object.entries(rules).filter(([, value]) => !!value);

                    if (!ruleItems.length) return null;

                    return (
                      <Stack key={ruleGroup} gap="0.5rem">
                        <Typography variant="subtitle2">{capitalizeFirstLetter(ruleGroup)}</Typography>

                        <Stack gap={1}>
                          {ruleItems.map(([rule, value]: [string, unknown]) => (
                            <Stack key={rule} gap={0.5}>
                              <Typography variant="caption" fontWeight={600}>
                                {capitalizeFirstLetter(rule)}
                              </Typography>

                              {Array.isArray(value) ? (
                                <ExpandableList
                                  showAllButton
                                  initialCount={50}
                                  incrementCount={50}
                                  items={value as string[]}
                                  getKey={item => item}
                                  renderItem={item => <Chip size="small" label={resolveLabel(item)} />}
                                />
                              ) : (
                                <Chip size="small" label={resolveLabel(value as string)} />
                              )}
                            </Stack>
                          ))}
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              </Stack>
            </RenderWithCondition>

            <RenderWithCondition condition={!!charges.length}>
              <Divider />

              <Stack gap="0.5rem">
                <Typography variant="h6" component="span">
                  Charges & Fees
                </Typography>

                <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap="1rem">
                  {charges.map((charge, idx) => (
                    <ChargeCard key={idx} charge={charge} />
                  ))}
                </Box>
              </Stack>
            </RenderWithCondition>

            <RenderWithCondition condition={!!taxes.length}>
              <Divider />

              <Stack gap="0.5rem">
                <Typography variant="h6" component="span">
                  Tax
                </Typography>

                <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap="1rem">
                  {taxes.map((tax, idx) => (
                    <TaxCard key={idx} tax={tax} />
                  ))}
                </Box>
              </Stack>
            </RenderWithCondition>
          </Stack>
        </RenderWithCondition>
      </RenderWithFallback>
    </CustomDialog>
  );
};

const ChargeCard = ({ charge }: { charge: ChargeItem }) => (
  <Box
    sx={{
      p: 2,
      width: '100%',
      borderRadius: '8px',
      border: `1px solid ${grey[200]}`,
      backgroundColor: grey[50],
    }}
  >
    <Stack gap="0.5rem">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" fontWeight={600}>
          {charge.name}
        </Typography>

        <Chip
          size="small"
          color={charge.isTaxable ? 'success' : 'default'}
          label={<Typography variant="body2">{charge.isTaxable ? 'Taxable' : 'Non-taxable'}</Typography>}
        />
      </Stack>

      {charge.description && (
        <Typography variant="body2" color="text.secondary">
          {charge.description}
        </Typography>
      )}

      <Stack direction="row" gap="1rem" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Type:{' '}
          <Box component="span" fontWeight={500} color="text.primary">
            {capitalizeFirstLetter(charge.amountType)}
          </Box>
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Amount:{' '}
          <Box component="span" fontWeight={600} color="text.primary">
            {charge.amountType === 'flat' ? formatCurrency(charge.amount) : `${charge.amount}%`}
          </Box>
        </Typography>
      </Stack>
    </Stack>
  </Box>
);

const TaxCard = ({ tax }: { tax: ChargeItem }) => (
  <Box
    sx={{
      p: 2,
      width: '100%',
      borderRadius: '8px',
      backgroundColor: grey[50],
      border: `1px solid ${grey[200]}`,
    }}
  >
    <Stack gap="0.5rem">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" fontWeight={600}>
          {tax.name}
        </Typography>

        <Typography variant="subtitle2" fontWeight={600} color="primary">
          {tax.amount}%
        </Typography>
      </Stack>

      {tax.description && (
        <Typography variant="body2" color="text.secondary">
          {tax.description}
        </Typography>
      )}
    </Stack>
  </Box>
);
